import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Grid,
  Paper,
  Tooltip,
  Fade,
  Zoom,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Popover,
  List,
  ListItem,
  ListItemIcon,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { fetchShifts, postShift } from "../actions/ShiftAction";


const RosterScheduler = () => {
  const dispatch = useDispatch();
  const { shifts, loading: shiftsLoading } = useSelector((state) => state.shifts);
  
  const [currentDate, setCurrentDate] = useState(new Date(2024, 0, 1));
  const [dateCards, setDateCards] = useState([]);
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [openDialogDateCard, setOpenDialogDateCard] = useState(false);
  const [newCardName, setNewCardName] = useState("");
  const [selectedShiftsForNewCard, setSelectedShiftsForNewCard] = useState([]);
  const [editingCardId, setEditingCardId] = useState(null);
  const [editingCardName, setEditingCardName] = useState("");
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openShiftDialog, setOpenShiftDialog] = useState(false);
  const [openAddShiftDialog, setOpenAddShiftDialog] = useState(false);
  const [newShiftCode, setNewShiftCode] = useState("");
  const [newShiftName, setNewShiftName] = useState("");
  const [newStartTime, setNewStartTime] = useState("");
  const [newEndTime, setNewEndTime] = useState("");
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());
  const [anchorElForCard, setAnchorElForCard] = useState(null);
  const [popoverCardId, setPopoverCardId] = useState(null);

  useEffect(() => {
    dispatch(fetchShifts());
  }, [dispatch]);

  // Handle year change
  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    setCurrentDate(new Date(year, selectedMonth, 1));
    setSelectedDates(new Set());
  };

  // Handle month change
  const handleMonthChange = (event) => {
    const month = event.target.value;
    setSelectedMonth(month);
    setCurrentDate(new Date(selectedYear, month, 1));
    setSelectedDates(new Set());
  };

  // Get days in month
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  // Get first day of month
  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // Handle date selection
  const handleDateClick = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

    setSelectedDates((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(dateStr)) {
        newSet.delete(dateStr);
      } else {
        newSet.add(dateStr);
      }
      return newSet;
    });
  };

  // Create new date card
  const handleCreateDateCard = () => {
    if (!newCardName.trim() || selectedDates.size === 0) {
      alert("Please enter a card name and select at least one date");
      return;
    }

    const newCard = {
      id: Date.now(),
      name: newCardName,
      dates: Array.from(selectedDates),
      selectedShifts: selectedShiftsForNewCard,
      createdAt: new Date(),
    };

    setDateCards((prev) => [...prev, newCard]);
    setNewCardName("");
    setSelectedDates(new Set());
    setSelectedShiftsForNewCard([]);
    setOpenDialogDateCard(false);
  };

  // Delete date card
  const handleDeleteCard = (id) => {
    if (window.confirm("Are you sure you want to delete this card?")) {
      setDateCards((prev) => prev.filter((card) => card.id !== id));
    }
  };

  // Edit card name
  const handleEditCard = (id, currentName) => {
    setEditingCardId(id);
    setEditingCardName(currentName);
    setOpenEditDialog(true);
  };

  // Save edited card name
  const handleSaveEditCard = () => {
    if (!editingCardName.trim()) {
      alert("Please enter a card name");
      return;
    }

    setDateCards((prev) =>
      prev.map((card) =>
        card.id === editingCardId ? { ...card, name: editingCardName } : card
      )
    );
    setOpenEditDialog(false);
    setEditingCardId(null);
    setEditingCardName("");
  };

  // Validate new shift form
  const validateShiftForm = () => {
    if (!newShiftCode.trim() || !newShiftName.trim() || !newStartTime || !newEndTime) {
      alert("All fields are required");
      return false;
    }
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if (!timeRegex.test(newStartTime) || !timeRegex.test(newEndTime)) {
      alert("Times must be in HH:MM format");
      return false;
    }
    if (newStartTime >= newEndTime) {
      alert("Start time must be before end time");
      return false;
    }
    return true;
  };

  // Handle add new shift
  const handleAddShift = () => {
    if (!validateShiftForm()) return;
    const shiftData = {
      shift_code: newShiftCode,
      shift_name: newShiftName,
      StartTime: newStartTime,
      EndTime: newEndTime,
      status: "A",
    };
    dispatch(postShift(shiftData));
    setNewShiftCode("");
    setNewShiftName("");
    setNewStartTime("");
    setNewEndTime("");
    setOpenAddShiftDialog(false);
  };

  // Remove date from card
  const handleRemoveDateFromCard = (cardId, dateToRemove) => {
    setDateCards((prev) =>
      prev.map((card) =>
        card.id === cardId
          ? {
              ...card,
              dates: card.dates.filter((date) => date !== dateToRemove),
            }
          : card
      )
    );
  };

  // Handle shift selection for a card
  const handleShiftSelect = (cardId, shiftId) => {
    setDateCards((prev) =>
      prev.map((card) =>
        card.id === cardId ? { ...card, selectedShift: shiftId } : card
      )
    );
  };

  // Change month
  const handlePrevMonth = () => {
    const newMonth = selectedMonth - 1;
    if (newMonth < 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
      setCurrentDate(new Date(selectedYear - 1, 11, 1));
    } else {
      setSelectedMonth(newMonth);
      setCurrentDate(new Date(selectedYear, newMonth, 1));
    }
    setSelectedDates(new Set());
  };

  const handleNextMonth = () => {
    const newMonth = selectedMonth + 1;
    if (newMonth > 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
      setCurrentDate(new Date(selectedYear + 1, 0, 1));
    } else {
      setSelectedMonth(newMonth);
      setCurrentDate(new Date(selectedYear, newMonth, 1));
    }
    setSelectedDates(new Set());
  };

  // Format date
  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split("-");
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Calendar rendering
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <Box
          key={`empty-${i}`}
          sx={{
            aspectRatio: "1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      );
    }

    // Days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const isSelected = selectedDates.has(dateStr);

      days.push(
        <Zoom key={day} in={true} style={{ transitionDelay: `${day * 10}ms` }}>
          <Paper
            onClick={() => handleDateClick(day)}
            sx={{
              aspectRatio: "1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              borderRadius: 2,
              backgroundColor: isSelected ? "#2c5aa0" : "#f0f7ff",
              color: isSelected ? "white" : "#5a7a94",
              fontWeight: isSelected ? "bold" : "500",
              transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
              border: "2px solid transparent",
              "&:hover": {
                backgroundColor: isSelected ? "#1e3a5f" : "#e8f1ff",
                transform: "scale(1.08)",
                border: "2px solid #2c5aa0",
                boxShadow: "0 4px 12px rgba(44, 90, 160, 0.2)",
              },
              fontSize: "0.95rem",
            }}
          >
            {day}
          </Paper>
        </Zoom>
      );
    }

    return days;
  };

  const monthYear = currentDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #e8f1f8 100%)",
        p: { xs: 2, sm: 3, md: 4 },
        overflowY: "scroll",
      }}
    >
      <Box sx={{ maxWidth: 1600, mx: "auto", width: "100%" }}>
          {/* Header */}
          <Fade in={true}>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #2c5aa0 0%, #1e3a5f 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 1,
                  fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3rem" },
                }}
              >
               Dalada Maligawa Roster Schedule Planner
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#5a7a94",
                  fontSize: "1.1rem",
                }}
              >
                Create and manage employee duty schedules with ease
              </Typography>
            </Box>
          </Fade>

          {/* Main Content */}
          <Grid container spacing={3}>
            {/* Calendar Section */}
            <Grid item xs={12} md={7}>
              <Fade in={true} style={{ transitionDelay: "100ms" }}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 4px 20px rgba(44, 90, 160, 0.08)",
                    background: "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
                    height: "100%",
                    border: "1px solid rgba(44, 90, 160, 0.1)",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    {/* Month/Year Selector with Dropdowns */}
                    <Box
                      sx={{
                        mb: 4,
                        p: 2.5,
                        borderRadius: 2,
                        background: "linear-gradient(135deg, #e8f1ff 0%, #f0f8ff 100%)",
                        border: "1px solid rgba(44, 90, 160, 0.15)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#2c5aa0",
                          mb: 2,
                          fontSize: "0.9rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Quick Selection
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={selectedMonth}
                            onChange={handleMonthChange}
                            sx={{
                              borderRadius: 2,
                              background: "white",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(44, 90, 160, 0.3)",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#2c5aa0",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#2c5aa0",
                              },
                            }}
                          >
                            {[
                              "January",
                              "February",
                              "March",
                              "April",
                              "May",
                              "June",
                              "July",
                              "August",
                              "September",
                              "October",
                              "November",
                              "December",
                            ].map((month, index) => (
                              <MenuItem key={index} value={index}>
                                {month}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 110 }}>
                          <Select
                            value={selectedYear}
                            onChange={handleYearChange}
                            sx={{
                              borderRadius: 2,
                              background: "white",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(44, 90, 160, 0.3)",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#2c5aa0",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#2c5aa0",
                              },
                            }}
                          >
                            {Array.from(
                              { length: 10 },
                              (_, i) => new Date().getFullYear() - 5 + i
                            ).map((year) => (
                              <MenuItem key={year} value={year}>
                                {year}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>

                    {/* Month Navigation Buttons */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                      }}
                    >
                      <Button
                        onClick={handlePrevMonth}
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          color: "#2c5aa0",
                          borderColor: "rgba(44, 90, 160, 0.3)",
                          fontSize: "0.9rem",
                          padding: "8px 16px",
                          textTransform: "none",
                          fontWeight: 500,
                          "&:hover": {
                            backgroundColor: "rgba(44, 90, 160, 0.05)",
                            borderColor: "#2c5aa0",
                          },
                        }}
                      >
                        ← Previous
                      </Button>

                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          background: "linear-gradient(135deg, #2c5aa0 0%, #1e3a5f 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          fontSize: { xs: "1.3rem", sm: "1.5rem" },
                        }}
                      >
                        {monthYear}
                      </Typography>

                      <Button
                        onClick={handleNextMonth}
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          color: "#2c5aa0",
                          borderColor: "rgba(44, 90, 160, 0.3)",
                          fontSize: "0.9rem",
                          padding: "8px 16px",
                          textTransform: "none",
                          fontWeight: 500,
                          "&:hover": {
                            backgroundColor: "rgba(44, 90, 160, 0.05)",
                            borderColor: "#2c5aa0",
                          },
                        }}
                      >
                        Next →
                      </Button>
                    </Box>

                    {/* Weekday Headers */}
                    <Grid
                      container
                      spacing={1}
                      sx={{ mb: 2 }}
                    >
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                        (day) => (
                          <Grid item xs={12 / 7} key={day}>
                            <Box
                              sx={{
                                textAlign: "center",
                                fontWeight: 700,
                                color: "#5a7a94",
                                fontSize: "0.85rem",
                                mb: 1,
                              }}
                            >
                              {day}
                            </Box>
                          </Grid>
                        )
                      )}
                    </Grid>

                    {/* Calendar Grid */}
                    <Grid container spacing={1}>
                      {renderCalendar().map((day, idx) => (
                        <Grid item xs={12 / 7} key={idx}>
                          {day}
                        </Grid>
                      ))}
                    </Grid>

                    {/* Selected Dates Display */}
                    {selectedDates.size > 0 && (
                      <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid rgba(44, 90, 160, 0.15)" }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: "#2c5aa0",
                            fontWeight: 600,
                            mb: 1.5,
                          }}
                        >
                          Selected Dates ({selectedDates.size})
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {Array.from(selectedDates).sort().map((date) => (
                            <Chip
                              key={date}
                              label={formatDate(date)}
                              onDelete={() =>
                                setSelectedDates((prev) => {
                                  const newSet = new Set(prev);
                                  newSet.delete(date);
                                  return newSet;
                                })
                              }
                              sx={{
                                backgroundColor: "#2c5aa0",
                                color: "white",
                                fontWeight: 600,
                                borderRadius: 2,
                                "& .MuiChip-deleteIcon": {
                                  color: "rgba(255, 255, 255, 0.7)",
                                  "&:hover": {
                                    color: "white",
                                  },
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Create Card Button */}
                    {selectedDates.size > 0 && (
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenDialogDateCard(true)}
                        sx={{
                          mt: 3,
                          backgroundColor: "#2c5aa0",
                          color: "white",
                          borderRadius: 2,
                          padding: "12px",
                          fontSize: "1rem",
                          fontWeight: 600,
                          textTransform: "none",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            backgroundColor: "#1e3a5f",
                            boxShadow: "0 8px 24px rgba(44, 90, 160, 0.3)",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        Create Date Card
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Fade>
            </Grid>

            {/* Date Cards Section */}
            <Grid item xs={12} md={5}>
              {/* View All Shifts Button */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<VisibilityIcon />}
                  onClick={() => setOpenShiftDialog(true)}
                  sx={{
                    backgroundColor: "#2c5aa0",
                    color: "white",
                    borderRadius: 2,
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: "0 4px 12px rgba(44, 90, 160, 0.3)",
                    "&:hover": {
                      backgroundColor: "#1e3a5f",
                      boxShadow: "0 6px 16px rgba(44, 90, 160, 0.4)",
                    },
                  }}
                >
                  View All Shifts
                </Button>
              </Box>

              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    background: "linear-gradient(135deg, #2c5aa0 0%, #1e3a5f 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    mb: 2,
                  }}
                >
                  Date Cards ({dateCards.length})
                </Typography>

                {dateCards.length === 0 ? (
                  <Card
                    sx={{
                      borderRadius: 3,
                      boxShadow: "0 4px 20px rgba(44, 90, 160, 0.08)",
                      background: "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
                      p: 3,
                      textAlign: "center",
                      border: "1px solid rgba(44, 90, 160, 0.1)",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#5a7a94",
                        fontSize: "1rem",
                      }}
                    >
                      No date cards created yet. Select dates from the calendar
                      and create your first card!
                    </Typography>
                  </Card>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {dateCards.map((card) => (
                      <Zoom key={card.id} in={true}>
                        <Card
                          sx={{
                            borderRadius: 3,
                            boxShadow: "0 4px 20px rgba(44, 90, 160, 0.08)",
                            background: "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
                            transition: "all 0.3s ease",
                            border: "1px solid rgba(44, 90, 160, 0.1)",
                            "&:hover": {
                              boxShadow: "0 8px 24px rgba(44, 90, 160, 0.15)",
                              transform: "translateY(-4px)",
                            },
                          }}
                        >
                          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                            {/* Card Header */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                mb: 2,
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    color: "#2c5aa0",
                                    wordBreak: "break-word",
                                    pr: 1,
                                  }}
                                >
                                  {card.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#5a7a94",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  {card.dates.length} dates
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                <Tooltip title="Edit name">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleEditCard(card.id, card.name)
                                    }
                                    sx={{
                                      color: "#2c5aa0",
                                      "&:hover": {
                                        backgroundColor: "rgba(44, 90, 160, 0.1)",
                                      },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete card">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteCard(card.id)}
                                    sx={{
                                      color: "#ff6b6b",
                                      "&:hover": {
                                        backgroundColor: "rgba(255, 107, 107, 0.1)",
                                      },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>

                            {/* Dates */}
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                              {card.dates.map((date) => (
                                <Chip
                                  key={date}
                                  label={formatDate(date)}
                                  onDelete={() =>
                                    handleRemoveDateFromCard(card.id, date)
                                  }
                                  size="small"
                                  sx={{
                                    backgroundColor: "rgba(44, 90, 160, 0.1)",
                                    color: "#2c5aa0",
                                    fontWeight: 500,
                                    borderRadius: 2,
                                    "& .MuiChip-deleteIcon": {
                                      color: "#5a7a94",
                                      "&:hover": {
                                        color: "#2c5aa0",
                                      },
                                    },
                                  }}
                                />
                              ))}
                            </Box>

                            

                            {/* Selected Shift Badge */}
                            {card.selectedShift && (
                              <Box sx={{ mt: 2 }}>
                                <Chip
                                  label={`Shift: ${shifts.find(s => s.shift_code === card.selectedShift)?.shift_name || 'Unknown'}`}
                                  sx={{
                                    backgroundColor: "#2c5aa0",
                                    color: "white",
                                    fontWeight: 600,
                                    borderRadius: 2,
                                  }}
                                />
                              </Box>
                            )}

                            {/* Assigned Shifts (for cards created with multiple shifts) */}
                            {card.selectedShifts && card.selectedShifts.length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#5a7a94", fontWeight: 600, mb: 1 }}
                                >
                                  Assigned Shifts:
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                  {card.selectedShifts.map((code) => {
                                    const s = shifts.find((sh) => sh.shift_code === code);
                                    const label = s ? `${s.shift_name} (${s.StartTime} - ${s.EndTime})` : code;
                                    return (
                                      <Chip
                                        key={code}
                                        label={label}
                                        size="small"
                                        sx={{
                                          backgroundColor: "#2c5aa0",
                                          color: "white",
                                          fontWeight: 600,
                                          borderRadius: 2,
                                        }}
                                      />
                                    );
                                  })}
                                </Box>
                              </Box>
                            )}
                            {/* Add button to open popover for adding more shifts */}
                            <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-start" }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  setAnchorElForCard(e.currentTarget);
                                  setPopoverCardId(card.id);
                                }}
                                sx={{
                                  backgroundColor: "rgba(44, 90, 160, 0.08)",
                                  color: "#2c5aa0",
                                  '&:hover': { backgroundColor: "rgba(44,90,160,0.12)" },
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </CardContent>
                        </Card>
                      </Zoom>
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>

        {/* Dialog for creating new card */}
        <Dialog
          open={openDialogDateCard}
          onClose={() => setOpenDialogDateCard(false)}
          maxWidth="sm"
          fullWidth
        >
            <DialogTitle
              sx={{
                fontWeight: 700,
                color: "#2c5aa0",
                fontSize: "1.3rem",
              }}
            >
              Create New Date Card
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Card Name"
                placeholder="e.g., Morning Shift, Team A, Week 1"
                value={newCardName}
                onChange={(e) => setNewCardName(e.target.value)}
                variant="outlined"
                sx={{
                  mt: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: "#2c5aa0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2c5aa0",
                    },
                  },
                }}
              />

              {/* Multi-select dropdown populated from state.shifts */}
              <FormControl fullWidth sx={{ mt: 2 }} size="small">
                <Typography
                  variant="body2"
                  sx={{ color: "#5a7a94", fontWeight: 600, mb: 1 }}
                >
                  Assign Shifts
                </Typography>
                <Select
                  multiple
                  displayEmpty
                  value={selectedShiftsForNewCard}
                  onChange={(e) => setSelectedShiftsForNewCard(e.target.value)}
                  renderValue={(selected) =>
                    selected && selected.length > 0
                      ? selected
                          .map((code) => {
                            const s = shifts.find((sh) => sh.shift_code === code);
                            return s ? `${s.shift_name} (${s.StartTime} - ${s.EndTime})` : code;
                          })
                          .join(", ")
                      : <em>No shifts selected</em>
                  }
                  MenuProps={{ PaperProps: { style: { maxHeight: 48 * 6 } } }}
                  sx={{
                    borderRadius: 2,
                    background: "white",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(44, 90, 160, 0.3)",
                    },
                  }}
                >
                  {shiftsLoading && (
                    <MenuItem disabled>
                      <CircularProgress size={18} />
                      <ListItemText primary=" Loading..." />
                    </MenuItem>
                  )}
                  {shifts.map((shift) => (
                    <MenuItem key={shift.shift_code} value={shift.shift_code}>
                      <Checkbox checked={selectedShiftsForNewCard.indexOf(shift.shift_code) > -1} />
                      <ListItemText primary={`${shift.shift_name} (${shift.StartTime} - ${shift.EndTime})`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ mt: 2, p: 2, backgroundColor: "rgba(44, 90, 160, 0.05)", borderRadius: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#2c5aa0",
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Selected Dates: {selectedDates.size}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {Array.from(selectedDates)
                    .sort()
                    .map((date) => (
                      <Chip
                        key={date}
                        label={formatDate(date)}
                        size="small"
                        sx={{
                          backgroundColor: "#2c5aa0",
                          color: "white",
                          fontWeight: 600,
                        }}
                      />
                    ))}
                </Box>
              </Box>

              {/* Selected Shifts Display */}
              <Box sx={{ mt: 2, p: 2, backgroundColor: "rgba(44, 90, 160, 0.03)", borderRadius: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "#2c5aa0", fontWeight: 600, mb: 1 }}
                >
                  Selected Shifts: {selectedShiftsForNewCard.length}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {selectedShiftsForNewCard.length === 0 ? (
                    <Typography variant="caption" sx={{ color: "#5a7a94" }}>
                      No shifts selected
                    </Typography>
                  ) : (
                    selectedShiftsForNewCard.map((code) => {
                      const s = shifts.find((sh) => sh.shift_code === code);
                      const label = s ? `${s.shift_name} (${s.StartTime} - ${s.EndTime})` : code;
                      return (
                        <Chip
                          key={code}
                          label={label}
                          size="small"
                          onDelete={() =>
                            setSelectedShiftsForNewCard((prev) => prev.filter((c) => c !== code))
                          }
                          sx={{
                            backgroundColor: "#2c5aa0",
                            color: "white",
                            fontWeight: 600,
                            borderRadius: 2,
                          }}
                        />
                      );
                    })
                  )}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setOpenDialogDateCard(false)}
                sx={{
                  color: "#5a7a94",
                  textTransform: "none",
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateDateCard}
                variant="contained"
                sx={{
                  backgroundColor: "#2c5aa0",
                  color: "white",
                  textTransform: "none",
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "#1e3a5f",
                  },
                }}
              >
                Create Card
              </Button>
            </DialogActions>
          </Dialog>

          {/* Popover for adding shifts to a specific card */}
          <Popover
            open={Boolean(anchorElForCard)}
            anchorEl={anchorElForCard}
            onClose={() => {
              setAnchorElForCard(null);
              setPopoverCardId(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Box sx={{ width: 320, p: 1 }}>
              <Typography sx={{ px: 1, py: 0.5, fontWeight: 700, color: '#2c5aa0' }}>
                Add Shifts
              </Typography>
              <List dense>
                {shifts.map((shift) => {
                  const card = dateCards.find((c) => c.id === popoverCardId);
                  const isSelected = card && card.selectedShifts && card.selectedShifts.includes(shift.shift_code);
                  return (
                    <ListItem
                      key={shift.shift_code}
                      button
                      onClick={() => {
                        setDateCards((prev) =>
                          prev.map((c) => {
                            if (c.id !== popoverCardId) return c;
                            const existing = c.selectedShifts || [];
                            if (existing.includes(shift.shift_code)) {
                              return { ...c, selectedShifts: existing.filter((x) => x !== shift.shift_code) };
                            }
                            return { ...c, selectedShifts: [...existing, shift.shift_code] };
                          })
                        );
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox edge="start" checked={isSelected} tabIndex={-1} disableRipple />
                      </ListItemIcon>
                      <ListItemText primary={`${shift.shift_name} (${shift.StartTime} - ${shift.EndTime})`} />
                    </ListItem>
                  );
                })}
              </List>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setAnchorElForCard(null);
                    setPopoverCardId(null);
                  }}
                >
                  Done
                </Button>
              </Box>
            </Box>
          </Popover>

        {/* Dialog for editing card name */}
        <Dialog
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          maxWidth="sm"
          fullWidth
        >
            <DialogTitle
              sx={{
                fontWeight: 700,
                color: "#2c5aa0",
                fontSize: "1.3rem",
              }}
            >
              Edit Card Name
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Card Name"
                value={editingCardName}
                onChange={(e) => setEditingCardName(e.target.value)}
                variant="outlined"
                sx={{
                  mt: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: "#2c5aa0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2c5aa0",
                    },
                  },
                }}
              />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setOpenEditDialog(false)}
                sx={{
                  color: "#5a7a94",
                  textTransform: "none",
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEditCard}
                variant="contained"
                sx={{
                  backgroundColor: "#2c5aa0",
                  color: "white",
                  textTransform: "none",
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "#1e3a5f",
                  },
                }}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Dialog for viewing all shifts */}
          <Dialog
            open={openShiftDialog}
            onClose={() => setOpenShiftDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle
              sx={{
                fontWeight: 700,
                color: "#2c5aa0",
                fontSize: "1.3rem",
              }}
            >
              All Available Shifts
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenAddShiftDialog(true)}
                  sx={{
                    backgroundColor: "#2c5aa0",
                    color: "white",
                    borderRadius: 3,
                    fontSize: "1.2rem",
                    fontWeight: 600,
                    padding: "12px 24px",
                    textTransform: "none",
                    boxShadow: "0 4px 12px rgba(44, 90, 160, 0.3)",
                    "&:hover": {
                      backgroundColor: "#1e3a5f",
                      boxShadow: "0 6px 16px rgba(44, 90, 160, 0.4)",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Add New Shift
                </Button>
              </Box>
              {shifts.length === 0 ? (
                <Typography>No shifts available</Typography>
              ) : (
                <Grid container spacing={2}>
                  {shifts.map((shift) => (
                    <Grid item xs={12} sm={6} md={4} key={shift.shift_code}>
                      <Card
                        sx={{
                          borderRadius: 2,
                          boxShadow: "0 2px 8px rgba(44, 90, 160, 0.1)",
                          border: "1px solid rgba(44, 90, 160, 0.1)",
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: "#2c5aa0" }}>
                            {shift.shift_name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#5a7a94", mt: 1 }}>
                            {shift.StartTime} - {shift.EndTime}
                          </Typography>
                          <Chip
                            label={shift.shift_code}
                            size="small"
                            sx={{
                              mt: 1,
                              backgroundColor: "rgba(44, 90, 160, 0.1)",
                              color: "#2c5aa0",
                            }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setOpenShiftDialog(false)}
                sx={{
                  color: "#5a7a94",
                  textTransform: "none",
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Dialog for adding new shift */}
          <Dialog
            open={openAddShiftDialog}
            onClose={() => setOpenAddShiftDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle
              sx={{
                fontWeight: 700,
                color: "#2c5aa0",
                fontSize: "1.3rem",
              }}
            >
              Add New Shift
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Shift Code"
                value={newShiftCode}
                onChange={(e) => setNewShiftCode(e.target.value)}
                variant="outlined"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Shift Name"
                value={newShiftName}
                onChange={(e) => setNewShiftName(e.target.value)}
                variant="outlined"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                value={newStartTime}
                onChange={(e) => setNewStartTime(e.target.value)}
                variant="outlined"
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="End Time"
                type="time"
                value={newEndTime}
                onChange={(e) => setNewEndTime(e.target.value)}
                variant="outlined"
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
              />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setOpenAddShiftDialog(false)}
                sx={{
                  color: "#5a7a94",
                  textTransform: "none",
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddShift}
                variant="contained"
                sx={{
                  backgroundColor: "#2c5aa0",
                  color: "white",
                  textTransform: "none",
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "#1e3a5f",
                  },
                }}
              >
                Add Shift
              </Button>
            </DialogActions>
          </Dialog>

        </Box>
      </Box>
  );
};

export default RosterScheduler;
